import React, { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import Button, { IButtonProps } from '../bootstrap/Button';
import Icon from '../icon/Icon';
import AuthContext from '../../contexts/authContext';
import useDarkMode from '../../hooks/useDarkMode';
import { demoPagesMenu } from '../../menu';
import ChangePasswordModal from './ChangePasswordModal';
import { useAccessControl } from '../../contexts/AccessControlContext';

/**
 * Generate a deterministic background gradient for avatar based on name.
 * Cycles through a palette of enterprise-friendly gradient pairs.
 */
const AVATAR_GRADIENTS = [
  ['#4f46e5', '#7c3aed'],
  ['#0ea5e9', '#0284c7'],
  ['#10b981', '#059669'],
  ['#f59e0b', '#d97706'],
  ['#ef4444', '#dc2626'],
  ['#8b5cf6', '#7c3aed'],
  ['#ec4899', '#db2777'],
  ['#06b6d4', '#0891b2'],
];

const getAvatarGradient = (name: string): string => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % AVATAR_GRADIENTS.length;
  const [from, to] = AVATAR_GRADIENTS[index];
  return `linear-gradient(135deg, ${from}, ${to})`;
};

const getInitials = (name: string, surname: string): string => {
  const first = name ? name.charAt(0).toUpperCase() : '';
  const last = surname ? surname.charAt(0).toUpperCase() : '';
  return first + last || '?';
};

const formatRole = (roles: string[] | undefined): string => {
  if (!roles || roles.length === 0) return 'User';
  const priority = ['ADMIN', 'PUBLISHER', 'EDITOR', 'AUTHOR'];
  for (const p of priority) {
    if (roles.includes(p)) return p.charAt(0) + p.slice(1).toLowerCase();
  }
  return roles[0].charAt(0) + roles[0].slice(1).toLowerCase();
};

interface IMenuItemProps {
  icon: string;
  label: string;
  description?: string;
  onClick: () => void;
  variant?: 'default' | 'danger';
  isDark: boolean;
}

const MenuItem: FC<IMenuItemProps> = ({ icon, label, description, onClick, variant = 'default', isDark }) => {
  const [hovered, setHovered] = useState(false);
  const isDanger = variant === 'danger';

  return (
    <button
      type='button'
      className='w-100 border-0 text-start d-flex align-items-center gap-3 px-3 py-2 rounded-2'
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered
          ? isDanger
            ? 'rgba(220,53,69,0.08)'
            : isDark
            ? 'rgba(255,255,255,0.06)'
            : 'rgba(79,70,229,0.06)'
          : 'transparent',
        cursor: 'pointer',
        transition: 'background 0.15s ease',
      }}>
      <span
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: hovered
            ? isDanger
              ? 'rgba(220,53,69,0.15)'
              : 'rgba(79,70,229,0.12)'
            : isDark
            ? 'rgba(255,255,255,0.08)'
            : 'rgba(0,0,0,0.05)',
          transition: 'background 0.15s ease',
        }}>
        <Icon
          icon={icon as Parameters<typeof Icon>[0]['icon']}
          size='sm'
          style={{ color: isDanger ? '#dc3545' : hovered ? '#4f46e5' : undefined }}
        />
      </span>
      <div className='flex-grow-1 overflow-hidden'>
        <div
          className='fw-semibold'
          style={{
            fontSize: '0.875rem',
            color: isDanger ? '#dc3545' : hovered ? '#4f46e5' : undefined,
            transition: 'color 0.15s ease',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
          {label}
        </div>
        {description && (
          <div className='text-muted' style={{ fontSize: '0.75rem' }}>
            {description}
          </div>
        )}
      </div>
    </button>
  );
};

/**
 * UserProfileDropdown — renders the authenticated user's avatar + name in the
 * top header bar, with a dropdown offering My Profile, Change Password, and Logout.
 *
 * Designed for the CommonHeaderRight component following the existing
 * Dropdown/Button patterns used throughout the Facit template.
 */
const UserProfileDropdown: FC<{ styledBtn: IButtonProps }> = ({ styledBtn }) => {
  const { userData, setUser } = useContext(AuthContext);
  const { clearAccessControl } = useAccessControl();
  const { darkModeStatus } = useDarkMode();
  const navigate = useNavigate();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isDropdownOpen]);

  const displayName = useMemo(() => {
    if (userData?.name && userData?.surname) {
      return `${userData.name} ${userData.surname}`;
    }
    return userData?.name || userData?.email || 'User';
  }, [userData]);

  const initials = useMemo(
    () => getInitials(userData?.name || '', userData?.surname || ''),
    [userData],
  );

  const avatarGradient = useMemo(() => getAvatarGradient(displayName), [displayName]);

  const role = useMemo(() => formatRole(userData?.roles), [userData]);

  const handleLogout = () => {
    if (setUser) setUser('');
    clearAccessControl();
    navigate(`../${demoPagesMenu.logout.path}`);
  };

  const handleMyProfile = () => {
    // Profile page navigation — extend when dedicated profile page is available
    navigate('/my-profile');
  };

  return (
    <>
      <div ref={dropdownRef} style={{ position: 'relative' }}>
        <button
          type='button'
          onClick={() => setIsDropdownOpen((prev) => !prev)}
          className={classNames(
            'btn d-flex align-items-center gap-2 px-2 py-1 rounded-pill',
            styledBtn.isLight ? 'btn-light' : 'btn-dark',
          )}
          style={{
            border: darkModeStatus
              ? '1px solid rgba(255,255,255,0.1)'
              : '1px solid rgba(0,0,0,0.08)',
            maxWidth: 220,
            transition: 'all 0.15s ease',
          }}
          aria-label='User profile menu'
          aria-expanded={isDropdownOpen}
          data-tour='user-profile'>
            {/* Avatar */}
            <span
              className='d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0'
              style={{
                width: 30,
                height: 30,
                background: avatarGradient,
                fontSize: '0.75rem',
                fontWeight: 700,
                color: '#fff',
                letterSpacing: '0.02em',
              }}>
              {initials}
            </span>
            {/* Name + role — hidden on small screens */}
            <span
              className='d-none d-md-flex flex-column align-items-start overflow-hidden'
              style={{ lineHeight: 1.2 }}>
              <span
                className='fw-semibold'
                style={{
                  fontSize: '0.8rem',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: 120,
                }}>
                {displayName}
              </span>
              <span
                className='text-muted'
                style={{ fontSize: '0.68rem', whiteSpace: 'nowrap' }}>
                {role}
              </span>
            </span>
            <Icon icon='KeyboardArrowDown' size='sm' className='text-muted ms-auto flex-shrink-0' />
          </button>

        {isDropdownOpen && (
          <div
            role='menu'
            style={{
              position: 'absolute',
              top: 'calc(100% + 6px)',
              right: 0,
              zIndex: 1050,
              minWidth: 260,
              borderRadius: 12,
              overflow: 'hidden',
              boxShadow: darkModeStatus
                ? '0 8px 32px rgba(0,0,0,0.5), 0 2px 8px rgba(0,0,0,0.3)'
                : '0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)',
              border: darkModeStatus ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.08)',
              background: darkModeStatus ? '#1e1e2e' : '#ffffff',
            }}>
          {/* Profile Card Header */}
          <div
            style={{
              background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
              padding: '16px',
            }}>
            <div className='d-flex align-items-center gap-3'>
              <span
                className='d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0'
                style={{
                  width: 46,
                  height: 46,
                  background: 'rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(8px)',
                  border: '2px solid rgba(255,255,255,0.3)',
                  fontSize: '1rem',
                  fontWeight: 700,
                  color: '#fff',
                  letterSpacing: '0.03em',
                }}>
                {initials}
              </span>
              <div className='overflow-hidden'>
                <div
                  className='fw-bold text-white'
                  style={{
                    fontSize: '0.9rem',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                  {displayName}
                </div>
                {userData?.email && (
                  <div
                    className='text-white-50'
                    style={{
                      fontSize: '0.73rem',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                    {userData.email}
                  </div>
                )}
                <span
                  className='badge mt-1'
                  style={{
                    fontSize: '0.65rem',
                    background: 'rgba(255,255,255,0.2)',
                    color: '#fff',
                    border: '1px solid rgba(255,255,255,0.3)',
                    padding: '2px 8px',
                    borderRadius: 20,
                    fontWeight: 500,
                  }}>
                  {role}
                </span>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className='p-2'>
            <MenuItem
              icon='AccountCircle'
              label='My Profile'
              description='View your account details'
              onClick={handleMyProfile}
              isDark={darkModeStatus}
            />
            <MenuItem
              icon='Lock'
              label='Change Password'
              description='Update your login credentials'
              onClick={() => setChangePasswordOpen(true)}
              isDark={darkModeStatus}
            />
          </div>

          {/* Divider */}
          <div
            style={{
              height: 1,
              background: darkModeStatus ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)',
              margin: '0 12px',
            }}
          />

          {/* Logout */}
          <div className='p-2'>
            <MenuItem
              icon='Logout'
              label='Logout'
              description='Sign out of your account'
              onClick={handleLogout}
              variant='danger'
              isDark={darkModeStatus}
            />
          </div>
        </div>
        )}
      </div>

      {/* Change Password Modal — mounted here so it sits outside the Dropdown z-index */}
      <ChangePasswordModal
        isOpen={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />
    </>
  );
};

export default UserProfileDropdown;
